import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/store';
import { useLazyGetLeaseQuery } from '@/store/api';
import { capitalizeFirstLetter } from '@/utils/common/formatting';

export type PaymentStatus = 'pending' | 'successful' | 'declined';

export type HistoryRow = {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'transaction' | 'payment';
  recipient?: string;
  /** Payment status - only applicable for payment type rows */
  status?: PaymentStatus;
  /** Original payment ID from the backend */
  paymentId?: string;
  /** Payment method description (e.g., "Visa *4242") */
  paymentMethod?: string;
  /** Convenience fee amount */
  convenienceFee?: number;
  /** Total amount including fees */
  totalAmount?: number;
};

export type SortField = 'description' | 'recipient' | 'date' | 'amount';
export type SortOrder = 'asc' | 'desc';

/**
 * Maps settlement status from backend to frontend display status
 */
const mapSettlementStatus = (settlementStatus: string | null): PaymentStatus => {
  if (!settlementStatus || settlementStatus === 'Pending Finalization') {
    return 'pending';
  }
  
  const lowerStatus = settlementStatus.toLowerCase();
  
  // Declined/rejected statuses
  if (
    lowerStatus.includes('rejected') ||
    lowerStatus.includes('declined') ||
    lowerStatus.includes('voided') ||
    lowerStatus.includes('failed')
  ) {
    return 'declined';
  }
  
  // Successful/settled statuses
  return 'successful';
};

export const useLeaseHistory = (leaseId?: number, userId?: number) => {
  const [triggerGetLease, { isFetching }] = useLazyGetLeaseQuery();
  const property = useAppSelector((s) => (s as any).property?.current);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [hasError, setHasError] = useState(false);
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!leaseId) return;

      try {
        setHasError(false);
        const includeTransactions = false;
        const includePayments = true;
        const includePending = true;

        const result = await triggerGetLease(
          { leaseId, includeTransactions, includePayments, includePending },
          false
        );

        if (!isMounted) return;

        if (result.error) {
          setHasError(true);
          return;
        }

        const resp = (result?.data as any) || {};
        const response = resp?.response || resp;

        const transactions: any[] = response?.transactions || [];
        const payments: any[] = response?.payments || [];

        const txRows: HistoryRow[] = transactions.map((t: any) => {
          const paymentToOwnAccount = userId === t.paidByUserId;
          const chargeCodeName = t.arCodeDescription || t.description;
          const paidByFullName = !paymentToOwnAccount && t.paidByFirstName && t.paidByLastName 
            ? `${t.paidByFirstName} ${t.paidByLastName}`
            : undefined;
          const type = t.amount > 0 ? 'Charge' : 'Payment';
          let description = '';
          if (t.arCodeDescription === 'Resio Fee') {
            description = 'Resio Convenience Fee';
          } else if (type === 'Payment' && paidByFullName) {
            description = `Payment - ${paidByFullName}`;
          } else if (type === 'Payment') {
            description = `Payment - ${capitalizeFirstLetter(chargeCodeName)}`;
          } else if (type === 'Charge') {
            description = `Charge - ${capitalizeFirstLetter(chargeCodeName)}`;
            if (new Date(t.transactionDate) > new Date()) {
              description += ' - Prepayment';
            }
          }
          return {
            id: `t-${t.id}`,
            date: t.transactionDate || t.postedAt || t.postDate || t.createdAt || '',
            description,
            amount: Number(t.amount ?? 0),
            type: 'transaction',
            recipient: paidByFullName,
          };
        });

        const paymentRows: HistoryRow[] = payments.map((p: any) => {
          const paymentToOwnAccount = userId === p.paidByUserId;
          let paidByFullName = '';
          if (!paymentToOwnAccount) {
            if (p.paidByFirstName && p.paidByLastName) {
              paidByFullName = `- ${p.paidByFirstName} ${p.paidByLastName}`;
            } else {
              paidByFullName = ' - by Linked Account';
            }
          }
          const chargeCodeName = p.arCodeDescription || p.description;
          const type = 'Payment';
          let description = '';
          if (p.arCodeDescription === 'Resio Fee') {
            description = 'Resio Convenience Fee';
          } else if (type === 'Payment' && paidByFullName) {
            description = `${type} ${paidByFullName}`;
          } else if (type === 'Payment') {
            description = `${type} - ${capitalizeFirstLetter(chargeCodeName)} `;
          }
          
          // Map settlement status to display status
          const status = mapSettlementStatus(p.settlementStatus);
          
          // Get payment method info
          const paymentMethod = p.paymentMethod || p.cardBrand 
            ? `${p.cardBrand || 'Card'} *${p.lastFour || '****'}`
            : undefined;
          
          // Calculate convenience fee and total
          const amount = Number(p.amount ?? 0);
          const convenienceFee = Number(p.convenienceFee ?? 0);
          const totalAmount = amount + convenienceFee;

          return {
            id: `p-${p.id}`,
            date: p.createdAt || p.postedAt || p.postDate || '',
            description: description.trim(),
            amount,
            type: 'payment',
            recipient: paidByFullName ? (p.paidByFirstName && p.paidByLastName ? `${p.paidByFirstName} ${p.paidByLastName}` : 'Linked Account') : undefined,
            status,
            paymentId: p.extTransactionId || p.id?.toString(),
            paymentMethod,
            convenienceFee,
            totalAmount,
          };
        });

        // Count pending payments
        const pendingCount = paymentRows.filter(p => p.status === 'pending').length;
        setPendingPaymentsCount(pendingCount);

        const combined = [...txRows, ...paymentRows];
        setRows(combined);
      } catch (_e) {
        if (isMounted) {
          setHasError(true);
          setRows([]);
          setPendingPaymentsCount(0);
        }
      }
    })();
    return () => { isMounted = false; };
  }, [leaseId, triggerGetLease, userId]);

  return { rows, hasError, isFetching, pendingPaymentsCount };
};

export const useSortableRows = (rows: HistoryRow[], sortField: SortField, sortOrder: SortOrder): HistoryRow[] => {
  return useMemo(() => {
    return [...rows].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      if (sortField === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
        const comparison = aVal.localeCompare(bVal);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    });
  }, [rows, sortField, sortOrder]);
};


